import type { AdminShopCatalog } from "../../../../api/shop";
import { Checkbox, ChipGroup, Field, FormGrid, Input, Select, Spinner, Textarea } from "../../../../ui";
import { normalizeSkuCode, PRODUCT_CODE_MAX } from "../../shared/codes";
import type { ProductForm } from "../editorModel";

export type BasicsSectionProps = {
  form: ProductForm;
  catalog: AdminShopCatalog;
  onChange: (values: Partial<ProductForm>) => void;
  /** suggest-code is running */
  suggesting: boolean;
  errorAt: (id: string) => string | null;
};

/** Editor §1: name, description, category → subcategory, sports, base price, product code, track stock, active. */
export function BasicsSection({ form, catalog, onChange, suggesting, errorAt }: BasicsSectionProps) {
  const category = catalog.categories.find((c) => c.id === form.categoryId);
  const subcategories = category?.subcategories ?? [];
  // A product can only be tagged with sports that have shop settings
  const sports = catalog.sports.filter((s) => s.isSetUp !== false);
  return (
    <section className="editor-section" data-target="section:basics">
      <h3 className="editor-section__title">Basics</h3>

      <Field label="Name" required error={errorAt("field:name")}>
        {(id) => (
          <Input
            id={id}
            value={form.name}
            maxLength={200}
            invalid={!!errorAt("field:name")}
            placeholder="e.g. Clash Home Jersey"
            onChange={(e) => onChange({ name: e.target.value })}
          />
        )}
      </Field>

      <Field label="Description">
        {(id) => (
          <Textarea
            id={id}
            value={form.description}
            rows={3}
            placeholder="Material, fit, what's included…"
            onChange={(e) => onChange({ description: e.target.value })}
          />
        )}
      </Field>

      <FormGrid columns={2}>
        <Field
          label="Category"
          required
          error={errorAt("field:category")}
          hint={catalog.categories.length === 0 ? "Add categories in Catalog setup first" : undefined}
        >
          {(id) => (
            <Select
              id={id}
              block
              invalid={!!errorAt("field:category")}
              value={form.categoryId}
              placeholder="Choose a category"
              onChange={(e) => onChange({ categoryId: e.target.value, subcategoryId: "" })}
            >
              {catalog.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {subcategories.length > 0 && (
          <Field label="Subcategory" required error={errorAt("field:subcategory")}>
            {(id) => (
              <Select
                id={id}
                block
                invalid={!!errorAt("field:subcategory")}
                value={form.subcategoryId}
                placeholder="Choose a subcategory"
                onChange={(e) => onChange({ subcategoryId: e.target.value })}
              >
                {subcategories.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </FormGrid>

      <Field
        label="Sports"
        hint={sports.length === 0 ? "Set up sports in Catalog setup to tag products" : "None picked = shown under every sport"}
      >
        <ChipGroup
          ariaLabel="Sports"
          options={sports.map((s) => ({ value: s.id, label: s.name }))}
          value={form.sportIds}
          onChange={(sportIds) => onChange({ sportIds })}
        />
      </Field>

      <FormGrid columns={2}>
        <Field label="Base price (EGP)" required error={errorAt("field:price")} hint="A SKU can override it under Combinations">
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              step="any"
              value={form.price}
              invalid={!!errorAt("field:price")}
              placeholder="0"
              rightSlot="EGP"
              onChange={(e) => onChange({ price: e.target.value })}
            />
          )}
        </Field>
        <Field
          label="Product code"
          error={errorAt("field:code")}
          aside={suggesting ? <Spinner size="sm" label="Suggesting a code" /> : undefined}
          hint={
            form.codeTouched
              ? "Builds the SKUs. Existing SKUs keep theirs until you rebuild them."
              : "Suggested from the category and sports. Edit it if you like."
          }
        >
          {(id) => (
            <Input
              id={id}
              value={form.code}
              maxLength={PRODUCT_CODE_MAX}
              invalid={!!errorAt("field:code")}
              placeholder="Generated on save"
              onChange={(e) => onChange({ code: normalizeSkuCode(e.target.value, PRODUCT_CODE_MAX), codeTouched: true })}
            />
          )}
        </Field>
      </FormGrid>

      <div className="editor-checks">
        <Checkbox
          checked={form.trackStock}
          onChange={(trackStock) => onChange({ trackStock })}
          label="Track stock"
          description={form.trackStock ? "Players can only buy what's in stock" : "Always available (made to order)"}
        />
        <Checkbox
          checked={form.isActive}
          onChange={(isActive) => onChange({ isActive })}
          label="Active"
          description={form.isActive ? "Shown in the shop on the website and app" : "Saved as a draft: hidden from the shop"}
        />
      </div>
    </section>
  );
}

export default BasicsSection;
